import React from "react";
import { Stack, Box, Typography, Divider, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import Logo from "../../shared/Logo/Logo";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HeaderBarcode from "../../shared/HeaderBarcode/HeaderBarcode";
import FooterBarcode from "../../shared/FooterBarcode/FooterBarcode";

import "./RulesPage.scss";

const RulesPage = () => {
  const navigate = useNavigate();

  const handleBack = React.useCallback(() => {
    navigate(-1); // Go back to the stored path or fallback
  }, [navigate]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleBack();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleBack]);

  return (
    <Stack className="rules-container">
      <Stack className="dotted-background">
        {/* Header */}
        <Stack direction="column" className="container header">
          <HeaderBarcode />

          {/* back button */}
          <Stack className="back-btn-wrapper">
            <IconButton
              className="btn btn-back btn-sm"
              variant="contained"
              onClick={() => handleBack()}
            >
              <ArrowBackIcon size={20} />
              <span>Go Back (esc)</span>
            </IconButton>
          </Stack>
        </Stack>

        {/* Content */}
        <Stack
          direction="column"
          className="container content"
          pt={2}
          spacing={2}
        >
          <Box className="notice-board" role="region" aria-label="Rules">
            <Stack
              sx={{ height: "600px", overflowY: "auto", paddingRight: "10px" }}
            >
              <Stack useFlexGap gap={6}>
                <Typography className="nb-title">Welcome</Typography>
                {/* welcome text */}
                <Stack useFlexGap gap={2}>
                  <Typography className="nb-body">
                    You are an Investigator with the WTUFO Task Force. A
                    mysterious drone has breached a secure UK military site.
                    Your mission is to uncover its secrets by solving
                    challenges, collecting clues, and piecing together the
                    truth.
                  </Typography>
                  <Typography>
                    Your curiosity, problem-solving, and persistence will decide
                    how much of the mystery you unravel.
                  </Typography>
                </Stack>
                {/* objective text */}
                <Stack useFlexGap gap={2}>
                  <Typography className="nb-title">Objective</Typography>
                  <ul className="nb-list">
                    <li className="nb-li-item">
                      Complete missions to investigate the drone. Earn coins and
                      collect clues.
                    </li>
                    <li className="nb-li-item">
                      The more you complete, the more of the story you’ll
                      reveal.
                    </li>
                    <li className="nb-li-item">
                      Piece the evidence together on your clue board.
                    </li>
                  </ul>
                </Stack>
                {/* getting start text */}
                <Stack>
                  <Typography className="nb-title">Getting Start</Typography>
                  <ol className="nb-list">
                    <li className="nb-li-item">
                      Open the game on your browser (best on desktop or laptop).
                    </li>
                    <li className="nb-li-item">
                      Generate your Investigator Code (auto-generated at login).
                    </li>
                    <li className="nb-li-item">
                      Choose your first mission from the map.
                    </li>
                  </ol>
                </Stack>
                {/* levels */}
                <Stack useFlexGap gap={3}>
                  <Stack>
                    <Typography className="nb-title">
                      Official Levels
                    </Typography>
                    <Typography>
                      Each mission has three difficulty levels:
                    </Typography>
                  </Stack>
                  <Stack useFlexGap gap={2}>
                    <Stack>
                      <Typography className="nb-sub-title">Rookie</Typography>
                      <Typography>
                        Ideal for those simply with enthusiasm to solve complex
                        problems and requires logical thinking and a bit of
                        determination.
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography className="nb-sub-title">
                        Intermediate
                      </Typography>
                      <Typography>
                        For those with some experience, providing a balanced
                        level of difficulty to sharpen your investigative
                        techniques.
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography className="nb-sub-title">Advanced</Typography>
                      <Typography>
                        A true test of your prowess, demanding precision and a
                        level of technical skill.
                      </Typography>
                    </Stack>
                  </Stack>
                  <Typography>
                    You can replay missions at different levels and collect more
                    coins. There are 4 missions in total.
                  </Typography>
                </Stack>
                {/*game basics */}
                <Stack>
                  <Typography className="nb-title">Game Basics</Typography>
                  <ul className="nb-list">
                    <li className="nb-li-item">
                      Pick a mission and select your difficulty.
                    </li>
                    <li className="nb-li-item">
                      Solve the challenge to unlock a coin and a clue.
                    </li>
                    <li className="nb-li-item">Coins track your progress.</li>
                    <li className="nb-li-item">
                      Clues are pinned to your personal Clue Board. Some are
                      real, others are decoys. Their meaning is revealed in the
                      endgame.
                    </li>
                  </ul>
                </Stack>
                {/*Coins and progression */}
                <Stack>
                  <Typography className="nb-title">
                    Coins & Progression
                  </Typography>
                  <ul className="nb-list">
                    <li className="nb-li-item">1 coin per completed level.</li>
                    <li className="nb-li-item">
                      3 coins available per mission (Rookie, Intermediate,
                      Advanced).
                    </li>
                    <li className="nb-li-item">
                      Advanced coins unlock the deepest insights.
                    </li>
                    <li className="nb-li-item">
                      Replay missions as many times as needed to collect all
                      coins.
                    </li>
                  </ul>
                </Stack>
                {/* Your tools */}
                <Stack>
                  <Typography className="nb-title">Your Tools</Typography>
                  <ul className="nb-list">
                    <li className="nb-li-item">
                      <span> Map:</span> Choose missions.
                    </li>

                    <li className="nb-li-item">
                      <span>Navigation:</span> Keyboard or mouse.
                    </li>
                    <li className="nb-li-item">
                      <span>Clue Board:</span> Review collected clues and
                      organise them.
                    </li>
                    <li className="nb-li-item">
                      <span>Profile:</span> Track coins earned.
                    </li>
                    <li className="nb-li-item">
                      <span>Settings:</span> Adjust your preferences.
                    </li>
                    <li className="nb-li-item">
                      <span>Pause:</span> Stop and return later — your progress
                      is saved to your investigator code.
                    </li>
                    <li className="nb-li-item">
                      <span>Exit:</span> Can only be triggered after 2 completed
                      levels and retires your investigator code forever.
                    </li>
                  </ul>
                </Stack>
                {/* End game*/}
                <Stack>
                  <Typography className="nb-title">End Game</Typography>
                  <Typography className="nb-body">
                    When you trade in your investigator code, all earned clues
                    will reveal the full story. You will learn which ones
                    identify the secrets and which were decoys. You will be
                    asked if you’d like to join the talent bank by providing
                    name and contact details. Those that complete to the highest
                    levels in the game, unlock additional exclusive content
                    including virtual meet and greets . Every level completion
                    will be encouraged to stay in touch with DSTL for potential
                    additional game play, learning and potential work
                    opportunities.
                  </Typography>
                </Stack>
                {/* tips for success*/}
                <Stack>
                  <Typography className="nb-title">Tips for Success</Typography>
                  <ul className="nb-list">
                    <li className="nb-li-item">
                      Pay attention — some clues mislead.
                    </li>
                    <li className="nb-li-item">Use replay to dig deeper.</li>
                    <li className="nb-li-item">Stay curious and persistent.</li>
                  </ul>
                </Stack>
                {/* support section*/}
                <Stack>
                  <Typography className="nb-title">Support</Typography>
                  <Typography>
                    If you experience technical issues, use the Help option in
                    the game menu.
                  </Typography>
                </Stack>
                <Divider color="#595959" />
                {/* credits and legal*/}
                <Stack>
                  <Typography className="nb-title">Credits & legal</Typography>
                  <Typography className="nb-body">
                    The game was created in collaboration with SMRS,
                    Gamification Nation and DSTL and cannot be copied or
                    recreated without written consent by the above parties. All
                    rights are reserved by the parties. All items, storyline and
                    objects in the game are fictional even if they may be
                    inspired by real life tools and events. All gameplay is
                    fictional.
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Stack>

        {/* Footer */}
        <Stack direction="row" className="container footer">
          <Logo />
          <FooterBarcode />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default RulesPage;
